import { Box, ContextualBanner } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import { ErrorMessage, Form, Formik, FormikConfig, FormikProps } from "formik";
import * as React from "react";
import { JSX } from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { object, string } from "yup";

import style from "./add-entitlement.scss";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import Info from "components/icons/Info";
import SimpleDeleteIcon from "components/icons/SimpleDeleteIcon";
import {
    checkIbrLicensesArePresent,
    createProductIdToNameMap,
    FEATURE_LICENSES,
    FEATURE_WORKFLOW_BDE,
    FEATURE_WORKFLOW_BMDE,
    FEATURE_WORKFLOW_LUN,
    fetchDynamicTierAddOns,
    fetchDynamicTierMigratedToCommonLicenses,
    HL_ONLY_ENTITLEMENT_LICENSE_TYPES,
    isAddOn,
    isMigratedToCommonLicense,
    isSubscriptionLicense,
    RESTRICTED_ENTITLEMENT_LICENSE_TYPES,
} from "components/licenses/common";
import { addMoreLicenses } from "components/licenses/delivery-history/DeliveryFormContent";
import { calculateBmcEolDaysLeft } from "components/licenses/licenseUtils";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { allowHlUpdate, allowSlUpdate } from "components/mcs/McsKebabMenu";
import StaticTable from "components/support/api-guide/StaticTable";
import { deduceTierLicenses, isTokenLicense } from "components/tenants/add-tenant/AddTenantForm";
import Tooltip from "components/tooltip/Tooltip";
import Heading from "components/typography/heading/Heading";
import { Container, EmsConfiguration, EntitlementType, LicenseData } from "domain/licenses";
import { FLAG_MANUAL_ENTITLEMENT_LIMITATION } from "services/feature/FeatureFlagService";
import { ProductRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { getCurrentTenantDetails, isLicensingModelBundleWithToken } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { isStringBlank, isStringNotBlank } from "utils/commonFunctions";
import { formatDateWithoutTime } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const DEFAULT_SELECT_CONTAINER_VALUE = "select_container";

interface Props {
    submitEventHandler: (values: FormValues, licenses: LicenseCount[]) => void;
    fetchedTokenRates?: ProductRateList[];
    emsConfiguration: EmsConfiguration;
    availableLicenses: LicenseData[];
    slContainers: Container[];
    mcUuid?: string;
    mcContainerId?: string;
    onDisableSubmit: (disabled: boolean) => void;
    preselectedProducts?: string[];
}

export interface LicenseCount {
    productId: string;
    available: number;
    assign: number;
    index: number;
    loading: boolean;
    tokenUsed: number;
    expirationDate: string;
    errorMessage?: JSX.Element;
}

export interface FormValues {
    type: string;
    description: string;
    tenantUuid?: string;
    containerId?: string;
}

const connector = connect((state: StoreState) => ({
    tenantUuid: state.userReducer.user?.tenantUuid,
    theme: state.themeReducer.theme,
}));

const PRODUCT_ID_TO_NAME = createProductIdToNameMap();
const IBR_FEATURES = [FEATURE_WORKFLOW_BDE, FEATURE_WORKFLOW_BMDE, FEATURE_WORKFLOW_LUN];

function createLicenseName(licenseType: string): string {
    return PRODUCT_ID_TO_NAME.get(licenseType) ?? "";
}

function AddEntitlementForm(props: Props & ConnectedProps<typeof connector>): JSX.Element {
    const { t } = useTranslation();
    const DESCRIPTION_MAX_LENGTH = 64;
    const MAXIMUM_ASSIGNED_ENTITLEMENTS = 300_000;
    const MAXIMUM_LICENSES_FOR_HL = 500; // Max licenses count for HL entitlements during manual entitlement creation

    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [availableLicenses, setAvailableLicenses] = React.useState<LicenseData[]>([]);
    const [blanccoTokenLicense, setBlanccoTokenLicense] = React.useState<LicenseData | undefined>();
    const [selectedLicenses, setSelectedLicenses] = React.useState<LicenseCount[]>([]);
    const [invalidAssignedAmount, setInvalidAssignedAmount] = React.useState(false);
    const theme = useTheme();
    const ALL_PRODUCTS = createProductIdToNameMap();
    const [initialTokenAmount, setInitialTokenAmount] = React.useState(0);
    const [errorFlag, setErrorFlag] = React.useState<boolean>(false);
    const manualEntitlementLimitation = useFeature(FLAG_MANUAL_ENTITLEMENT_LIMITATION);
    const [hlLimitError, setHlLimitError] = React.useState<boolean>(false);
    const [currentProduct, setCurrentProduct] = React.useState<string>();
    const [assignError, setAssignError] = React.useState<boolean>(false);
    const [entitlementType, setEntitlementType] = React.useState<EntitlementType>(
        manualEntitlementLimitation && isStringBlank(props.mcUuid)
            ? EntitlementType.HL_UPDATE
            : props.emsConfiguration.slEntitlements && props.slContainers.length > 0
              ? EntitlementType.SL_UPDATE
              : EntitlementType.HL_UPDATE
    );

    const beforeEol = calculateBmcEolDaysLeft() > 0;
    const isSlType = entitlementType === EntitlementType.SL_CREATE || entitlementType === EntitlementType.SL_UPDATE;
    const isForMc = () => {
        return isStringNotBlank(props.mcUuid);
    };
    const isCreationDisabled = isSlType && !beforeEol && !isForMc();

    React.useEffect(() => {
        reCalculateTokens();
    }, [props.fetchedTokenRates, selectedLicenses]);

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (values, { setSubmitting }) => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_ENTITLEMENT,
            action: Action.ADD_ENTITLEMENT,
        });
        await props.submitEventHandler(values, selectedLicenses);
        setSubmitting(true);
    };

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const isIncludedInEntitlementType = (licenseType: string) => {
        if (isTokenLicense(licenseType)) {
            return true;
        }
        if (entitlementType !== EntitlementType.HL_UPDATE) {
            return (
                !HL_ONLY_ENTITLEMENT_LICENSE_TYPES.includes(licenseType) &&
                !RESTRICTED_ENTITLEMENT_LICENSE_TYPES.includes(licenseType)
            );
        }
        return !RESTRICTED_ENTITLEMENT_LICENSE_TYPES.includes(licenseType);
    };

    const isValidLicense = (license: LicenseData) => {
        return (
            checkIbrLicensesArePresent(license.type) &&
            license.available > 0 &&
            new Date() < new Date(license.expirationDate) &&
            !isSubscriptionLicense(license.type) &&
            isIncludedInEntitlementType(license.type)
        );
    };
    React.useEffect(() => {
        const realProductRows: LicenseData[] = [];
        const syntheticTierRows: LicenseData[] = [];
        const deducedTierLicenses = deduceTierLicenses(getCurrentTenantDetails().tenantTier, true);
        props.availableLicenses.forEach((each: LicenseData) => {
            if (!isValidLicense(each)) {
                return;
            }
            if (!isTokenLicense(each.type) && isIncludedInEntitlementType(each.type)) {
                each.product = ALL_PRODUCTS.get(each.type) || each.product;
                realProductRows.push(each);
            } else {
                if (isLicensingModelBundleWithToken() && deducedTierLicenses) {
                    setBlanccoTokenLicense(each);
                    setInitialTokenAmount(each.available);
                    syntheticTierRows.push(
                        ...deducedTierLicenses
                            .filter(
                                (tierLicense) =>
                                    !FEATURE_LICENSES.includes(tierLicense) && isIncludedInEntitlementType(tierLicense)
                            )
                            .map((eachTierLicense) => {
                                return {
                                    product: ALL_PRODUCTS.get(eachTierLicense) || eachTierLicense,
                                    type: eachTierLicense,
                                    license: "",
                                    available: each.available,
                                    total: 0,
                                    expirationDate: each.expirationDate,
                                    licenseType: "",
                                    isFeatureLicensePresent: false,
                                };
                            })
                    );
                    IBR_FEATURES.every((featureLicense) => {
                        if (deducedTierLicenses.includes(featureLicense)) {
                            IBR_FEATURES.map((product) => {
                                syntheticTierRows.push({
                                    product: product,
                                    type: product,
                                    license: "",
                                    available: 0,
                                    total: 0,
                                    expirationDate: each.expirationDate,
                                    licenseType: "",
                                    isFeatureLicensePresent: false,
                                });
                            });
                        }
                    });
                }
            }
        });
        // A pid can be both a real add-on the tenant owns and, once migrated into the tier's
        // common licenses, a synthetic pseudo-row backed by the shared common-license/token pool.
        // Keep only the real row for those pids so the dropdown doesn't show the pid twice.
        const migratedPids = fetchDynamicTierMigratedToCommonLicenses(getCurrentTenantDetails().tenantTier);
        const dedupedSyntheticRows = syntheticTierRows.filter((synthetic) => {
            const isMigratedPid = migratedPids.includes(synthetic.type);
            const hasRealInventoryRow = realProductRows.some((real) => real.type === synthetic.type);
            return !(isMigratedPid && hasRealInventoryRow);
        });
        const tenantLicenses = [...realProductRows, ...dedupedSyntheticRows];
        const sortedTenantLicenses = tenantLicenses.sort((a, b) => a.product.localeCompare(b.product));
        setAvailableLicenses(sortedTenantLicenses);
        const defaultTenantLicense = sortedTenantLicenses[0];
        const filteredSelectedLicenses = selectedLicenses.filter(
            (selected) =>
                typeof sortedTenantLicenses.find((available) => available.type === selected.productId) !== "undefined"
        );
        if (filteredSelectedLicenses.length > 0) {
            setSelectedLicenses(filteredSelectedLicenses);
        } else {
            const defaultLicense = {
                productId: defaultTenantLicense.type,
                available: defaultTenantLicense.available,
                assign: 1,
                index: 0,
                loading: false,
                tokenUsed:
                    blanccoTokenLicense && !isAddOn(defaultTenantLicense.type)
                        ? fetchTokenRate(defaultTenantLicense.type)
                        : defaultTenantLicense.available,
                expirationDate: defaultTenantLicense.expirationDate,
            };
            if (!props.preselectedProducts || props.preselectedProducts.length < 1) {
                setSelectedLicenses([defaultLicense]);
            } else {
                const preselectedLicenses: LicenseCount[] = [];
                sortedTenantLicenses.forEach((each) => {
                    if (props.preselectedProducts?.includes(each.type)) {
                        preselectedLicenses.push({
                            productId: each.type,
                            available: each.available,
                            assign: 1,
                            index: preselectedLicenses.length,
                            loading: false,
                            tokenUsed:
                                blanccoTokenLicense && !isAddOn(each.type) ? fetchTokenRate(each.type) : each.available,
                            expirationDate: each.expirationDate,
                        });
                    }
                });
                if (preselectedLicenses.length > 0) {
                    setSelectedLicenses(preselectedLicenses);
                } else {
                    setSelectedLicenses([defaultLicense]);
                }
            }
        }
    }, [entitlementType]);

    React.useEffect(() => {
        if (blanccoTokenLicense) {
            setErrorFlag(blanccoTokenLicense.available < 0);
        }
    }, [blanccoTokenLicense?.available]);

    const changeLicenseType = (productId: string, index: number) => {
        const selected: LicenseData | undefined = availableLicenses.find((each) => each.type === productId);
        if (selected) {
            setSelectedLicenses(
                selectedLicenses.map((license) => {
                    if (license.index === index) {
                        license = {
                            productId: selected.type,
                            available: selected.available,
                            assign: 1,
                            index: index,
                            loading: false,
                            tokenUsed:
                                blanccoTokenLicense && !isAddOn(selected.type)
                                    ? fetchTokenRate(selected.type)
                                    : selected.available,
                            expirationDate: selected.expirationDate,
                        };
                    }
                    return license;
                })
            );
        }
    };

    const hideAddRowButton = () => {
        if (isLicensingModelBundleWithToken()) {
            // Filter add-ons
            const addOnsAvailable = availableLicenses.filter((license) => isAddOn(license.type));
            const addonsSelected = selectedLicenses.filter(
                (license) => isAddOn(license.productId) && license.available > 0
            );

            // Update available add-ons in a single pass
            addOnsAvailable.map((addOn) => {
                const selectedAddOn = addonsSelected.find((selected) => selected.productId === addOn.type);
                return selectedAddOn ? { ...addOn, available: addOn.available - selectedAddOn.assign } : addOn;
            });

            // Check for add-ons not selected
            const unselectedAddOns = addOnsAvailable.filter((addOn) => {
                const selectedAddOn = addonsSelected.find((selected) => selected.productId === addOn.type);
                return selectedAddOn?.available !== addOn.available;
            });

            if (blanccoTokenLicense) {
                return (
                    (blanccoTokenLicense.available <= 0 && unselectedAddOns.length === 0) ||
                    availableLicenses.length === selectedLicenses.length
                );
            } else {
                return unselectedAddOns.length === 0 || availableLicenses.length === selectedLicenses.length;
            }
        }

        return availableLicenses.length === selectedLicenses.length;
    };

    const addRow = () => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_ENTITLEMENT,
            action: Action.ADD_ENTITLEMENT_LICENSE,
        });

        // For the new row, first available license that was not selected previously will be assigned.
        let availableItem: LicenseData | undefined;
        availableLicenses.forEach((item) => {
            if (selectedLicenses.find((selectedLicense) => item.type === selectedLicense.productId) === undefined) {
                availableItem = item;
            }
        });

        // availableItem is undefined in case all the available licenses have been selected.
        // In this case, the button is hidden.
        setSelectedLicenses(
            selectedLicenses.concat([
                {
                    productId: availableItem?.type ?? "",
                    available: availableItem?.available ?? 0,
                    assign: 1,
                    index:
                        (selectedLicenses.length > 0 ? Math.max(...selectedLicenses.map((item) => item.index)) : 0) + 1,
                    loading: false,
                    tokenUsed:
                        blanccoTokenLicense && !isAddOn(availableLicenses[0].type)
                            ? fetchTokenRate(availableLicenses[0].type)
                            : availableLicenses[0].available,
                    expirationDate: availableItem?.expirationDate ?? "",
                },
            ])
        );
        if (availableItem?.type) {
            setCurrentProduct(availableItem.type);
        }
    };

    const clearAllRows = () => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_ENTITLEMENT,
            action: Action.REMOVE_ALL_LICENSES,
        });
        setSelectedLicenses([]);
        setErrorFlag(false);
    };

    const removeRow = (index: number) => {
        setSelectedLicenses(selectedLicenses.filter((license) => license.index !== index));
    };

    const createAssignAmountErrorMessage = (
        amount: number,
        available: number,
        index: number,
        forCommonLicense: boolean
    ) => {
        let message = "";
        if (isNaN(amount)) {
            message = t("AddEntitlementForm.table.assignEntitlementsRequired");
        }
        if (amount < 1) {
            message = t("AddEntitlementForm.table.minimumNumberOfAssignedEntitlements");
        }
        const maximum = Math.min(MAXIMUM_ASSIGNED_ENTITLEMENTS, available);
        if (amount > maximum) {
            setAssignError(true);
            setErrorFlag(forCommonLicense);
            message = t("AddEntitlementForm.table.maximumNumberOfAssignedEntitlements", {
                maximumAmount: maximum,
            });
        } else {
            setErrorFlag(false);
            setAssignError(false);
        }
        // First, check the amount is less than available as above
        // Then, check that the total sum is less than MAXIMUM_LICENSES_FOR_HL
        if (manualEntitlementLimitation) {
            if (
                isStringBlank(props.mcUuid) &&
                (entitlementType === EntitlementType.HL || entitlementType === EntitlementType.HL_UPDATE)
            ) {
                let totalAmount = 0;
                selectedLicenses.forEach((license) => {
                    totalAmount = totalAmount + license.assign;
                });
                setHlLimitError(totalAmount > MAXIMUM_LICENSES_FOR_HL);
            }
        }
        if (isStringBlank(message)) {
            setInvalidAssignedAmount(false);
            setSelectedLicenses(
                selectedLicenses.map((license) => {
                    if (license.index === index) {
                        license.errorMessage = <></>;
                    }
                    return license;
                })
            );
            return;
        }
        setInvalidAssignedAmount(true);
        setSelectedLicenses(
            selectedLicenses.map((license) => {
                if (license.index === index) {
                    license.errorMessage = (
                        <div
                            className={classNames(form.error, form.errorMultiLine)}
                            data-testid={
                                testIds.workArea.license.entitlements.createEntitlementDialog.licensesTable
                                    .assignedAmountInput.errorLabel
                            }
                        >
                            <span className={style.text}>{message}</span>
                        </div>
                    );
                }
                return license;
            })
        );
    };

    const reCalculateTokens = () => {
        if (blanccoTokenLicense) {
            if (selectedLicenses.length === 0) {
                setBlanccoTokenLicense((prevBlanccoTokenLicense: LicenseData) => {
                    return {
                        ...prevBlanccoTokenLicense,
                        available: initialTokenAmount,
                    };
                });
                setErrorFlag(false);
            }

            setBlanccoTokenLicense((prevBlanccoTokenLicense: LicenseData) => {
                const totalTokensToDeduct = selectedLicenses.reduce((accumulatedToken, license) => {
                    return accumulatedToken + computeCommonPoolConsumption(license.productId, license.assign);
                }, 0);

                return {
                    ...prevBlanccoTokenLicense,
                    available: initialTokenAmount - totalTokensToDeduct,
                };
            });
        }
    };

    const computeAvailableAmount = (availableLicenses: number, assignedAmount: number) => {
        if (isNaN(assignedAmount)) {
            return availableLicenses;
        } else {
            return availableLicenses - assignedAmount;
        }
    };

    const computeAvailableTokenAmount = (productId: string, assignedAmount: number) => {
        if (isNaN(assignedAmount)) {
            return 0;
        } else {
            return IBR_FEATURES.includes(productId) ? 0 : fetchTokenRate(productId) * assignedAmount;
        }
    };

    React.useEffect(() => {
        props.onDisableSubmit(
            selectedLicenses.length < 1 ||
                invalidAssignedAmount ||
                errorFlag ||
                assignError ||
                hlLimitError ||
                isCreationDisabled
        );
    }, [selectedLicenses, invalidAssignedAmount, errorFlag, assignError, isCreationDisabled]);

    const fetchTokenRate = (productId: string) => {
        if (IBR_FEATURES.includes(productId)) {
            return 1;
        }
        const product = props.fetchedTokenRates?.find((p) => p.productId === productId);
        return product ? Number(product.rate) : 0;
    };

    // Real add-on inventory the tenant owns for productId, independent of any synthetic
    // common-license row deduced from the tier config. Used only for pids flagged as migrated,
    // to determine how much of an assignment can be drawn from real inventory before it spills
    // into the shared common-license/token pool.
    const fetchAddonAvailableForProduct = (productId: string): number => {
        const realInventoryLicense = props.availableLicenses.find(
            (each) => each.type === productId && !isTokenLicense(each.type)
        );
        return realInventoryLicense?.available ?? 0;
    };

    const computeCommonPoolConsumption = (productId: string, assignedAmount: number): number => {
        if (isNaN(assignedAmount)) {
            return 0;
        }
        if (!isMigratedToCommonLicense(productId)) {
            return computeAvailableTokenAmount(productId, assignedAmount);
        }
        const overflowUnits = Math.max(0, assignedAmount - fetchAddonAvailableForProduct(productId));
        return overflowUnits * fetchTokenRate(productId);
    };

    const computeCombinedMaxAssignable = (productId: string, rowIndex: number): number => {
        const tokenRate = fetchTokenRate(productId);
        const addonAvailable = fetchAddonAvailableForProduct(productId);
        if (tokenRate === 0) {
            return addonAvailable;
        }
        const poolConsumedByOtherRows = selectedLicenses
            .filter((selected) => selected.index !== rowIndex)
            .reduce((sum, selected) => sum + computeCommonPoolConsumption(selected.productId, selected.assign), 0);
        const remainingPoolBeforeThisRow = Math.max(0, initialTokenAmount - poolConsumedByOtherRows);
        return addonAvailable + Math.floor(remainingPoolBeforeThisRow / tokenRate);
    };

    // Helper functions
    const renderTokenLicenseContent = (license: LicenseCount) => {
        if (isAddOn(license.productId)) {
            return <>{isNaN(license.assign) ? "0" : license.assign.toString()}</>;
        }
        return <div>{computeCommonPoolConsumption(license.productId, license.assign).toString()}</div>;
    };

    const renderNonTokenLicenseContent = (license: LicenseCount) => {
        return computeAvailableAmount(license.available, license.assign).toString();
    };

    function deducePredefinedEntitlementType(): string {
        if (!isForMc()) {
            return manualEntitlementLimitation
                ? EntitlementType.HL_UPDATE
                : props.emsConfiguration.slEntitlements && props.slContainers.length > 0
                  ? "SL_UPDATE"
                  : EntitlementType.HL_UPDATE;
        }
        if (!props.mcContainerId) {
            return EntitlementType.SL_CREATE;
        }
        if (allowSlUpdate(props.emsConfiguration, props.mcContainerId, props.slContainers)) {
            return EntitlementType.SL_UPDATE;
        }
        return EntitlementType.HL_UPDATE;
    }

    function deducePredefinedContainerId(): string {
        if (deducePredefinedEntitlementType() !== EntitlementType.SL_UPDATE) {
            return "";
        }
        if (!props.mcContainerId) {
            return props.slContainers.length > 0 ? props.slContainers[0].containerId : "";
        }
        return props.mcContainerId;
    }

    function deduceEntitlementOptions() {
        const translations: Map<EntitlementType, string> = new Map<EntitlementType, string>([
            [EntitlementType.SL_UPDATE, t("Entitlements.type.slUpdate")],
            [EntitlementType.HL_UPDATE, t("Entitlements.type.hlUpdate")],
            [EntitlementType.SL_CREATE, t("Entitlements.type.slCreate")],
        ]);

        if (manualEntitlementLimitation) {
            if (!isForMc()) {
                return new Map<EntitlementType, string>([[EntitlementType.HL_UPDATE, t("Entitlements.type.hlUpdate")]]);
            }
        }

        if (!props.mcContainerId) {
            return translations;
        }
        const allowedTypes: Map<EntitlementType, string> = new Map<EntitlementType, string>([]);

        if (allowSlUpdate(props.emsConfiguration, props.mcContainerId, props.slContainers)) {
            allowedTypes.set(EntitlementType.SL_UPDATE, translations.get(EntitlementType.SL_UPDATE) || "");
        }
        if (allowHlUpdate(props.emsConfiguration, props.slContainers, props.mcContainerId)) {
            allowedTypes.set(EntitlementType.HL_UPDATE, translations.get(EntitlementType.HL_UPDATE) || "");
        }
        return allowedTypes;
    }

    function createEntitlementOptions() {
        const options: JSX.Element[] = [];
        const allowedTypes = deduceEntitlementOptions();
        Object.keys(EntitlementType).forEach((each) => {
            const entitlementType: EntitlementType = each as EntitlementType;
            if (allowedTypes.get(entitlementType)) {
                options.push(
                    <option key={entitlementType} value={entitlementType}>
                        {allowedTypes.get(entitlementType)}
                    </option>
                );
            }
        });
        return options;
    }

    const bmcEolBanner = (
        <ContextualBanner variant={beforeEol ? "Warning" : "Error"}>
            {beforeEol ? (
                <>
                    <Trans i18nKey="Entitlements.bmcEolWarningTextShort" count={calculateBmcEolDaysLeft()} />
                    <a
                        href={
                            "https://support.blancco.com/space/NEWS/blog/333742134/Action+Required:+Upgrade+from+Blancco+Management+Console+to+Blancco+Management+Portal+On-Premise+Before+End+of+Life+%E2%80%93+April+23,+2026"
                        }
                        target={"_blank"}
                        rel="noopener noreferrer"
                    >
                        {t("Entitlements.bmcEolCtaInstructions")}
                    </a>
                </>
            ) : (
                <>
                    <Trans i18nKey="Entitlements.bmcEolErrorText" />
                    <a
                        href={
                            "https://portal.servicedesk.blancco.com/servicedesk/customer/user/login?destination=portals"
                        }
                        target={"_blank"}
                        rel="noopener noreferrer"
                    >
                        {t("Entitlements.bmcEolCtaSupport")}
                    </a>
                </>
            )}
        </ContextualBanner>
    );

    return (
        <Formik
            initialValues={{
                type: deducePredefinedEntitlementType(),
                description: "",
                containerId: deducePredefinedContainerId(),
            }}
            onSubmit={submitHandler}
            validationSchema={object().shape({
                type: string().required(t("AddEntitlementForm.typeRequired")),
                description: string().max(DESCRIPTION_MAX_LENGTH),
                containerId: string().when("type", {
                    is: EntitlementType.SL_UPDATE,
                    then: string()
                        .required(t("AddEntitlementForm.containerRequired"))
                        .test(
                            "Something selected",
                            t("AddEntitlementForm.containerRequired"),
                            (value) => value !== DEFAULT_SELECT_CONTAINER_VALUE
                        ),
                    otherwise: string().optional(),
                }),
            })}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({ values, errors, handleChange, handleBlur }: FormikProps<FormValues>) => {
                const cells = selectedLicenses.map((license) => {
                    let addOn = isAddOn(license.productId);
                    let migrated = isMigratedToCommonLicense(license.productId);

                    return [
                        <>
                            <select
                                className={classNames(style.formElement, form.select)}
                                id={"productId" + license.index}
                                name={"productId" + license.index}
                                key={"productId" + license.index}
                                onChange={(e) => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.LICENSE_ENTITLEMENT,
                                        action: Action.CHANGE_LICENSE_TYPE,
                                        label: license.productId,
                                    });
                                    changeLicenseType(e.target.value, license.index);
                                    addOn = isAddOn(e.target.value);
                                    migrated = isMigratedToCommonLicense(e.target.value);
                                }}
                                defaultValue={license.productId}
                                data-testid={
                                    testIds.workArea.license.entitlements.createEntitlementDialog.licensesTable
                                        .licenseTypeSelect
                                }
                            >
                                {availableLicenses.map((value) => (
                                    <option
                                        key={value.type}
                                        value={value.type}
                                        defaultValue={license.productId}
                                        hidden={
                                            value.type !== license.productId &&
                                            selectedLicenses.find((item) => item.productId === value.type) !== undefined
                                        }
                                    >
                                        {createLicenseName(value.type)}
                                    </option>
                                ))}
                            </select>
                            {isLicensingModelBundleWithToken() && migrated && (
                                <div className={classNames(style.addOnText)}>{t("Common.migrated")}</div>
                            )}
                            {isLicensingModelBundleWithToken() && addOn && !migrated && (
                                <div className={classNames(style.addOnText)}>{t("Common.addOns")}</div>
                            )}
                        </>,
                        license.loading ? (
                            <LoadingIndicator small={true} />
                        ) : blanccoTokenLicense ? (
                            renderTokenLicenseContent(license)
                        ) : (
                            renderNonTokenLicenseContent(license)
                        ),
                        <div key={license.index}>
                            <input
                                className={classNames(form.input, style.formElement, style.assignInput, {
                                    [form.inputError]:
                                        (license.productId === currentProduct && errorFlag) ||
                                        (isAddOn(license.productId) && assignError),
                                })}
                                key={"assign" + license.index}
                                id={"assign" + license.index}
                                type="number"
                                onChange={(e) => {
                                    let assignedValue = 0;
                                    setSelectedLicenses(
                                        selectedLicenses.map((element) => {
                                            if (element.productId === license.productId) {
                                                element.assign = parseInt(e.target.value);
                                                element.tokenUsed =
                                                    fetchTokenRate(license.productId) * parseInt(e.target.value);
                                            }
                                            if (
                                                !fetchDynamicTierAddOns(getCurrentTenantDetails().tenantTier).includes(
                                                    license.productId
                                                )
                                            ) {
                                                assignedValue += isMigratedToCommonLicense(element.productId)
                                                    ? Math.max(
                                                          0,
                                                          element.assign -
                                                              fetchAddonAvailableForProduct(element.productId)
                                                      )
                                                    : element.assign;
                                                if (initialTokenAmount < assignedValue) {
                                                    setCurrentProduct(license.productId);
                                                }
                                            }
                                            return element;
                                        })
                                    );
                                    if (
                                        !isLicensingModelBundleWithToken() ||
                                        (isLicensingModelBundleWithToken() && isAddOn(license.productId)) ||
                                        (isLicensingModelBundleWithToken() &&
                                            isMigratedToCommonLicense(license.productId)) ||
                                        (manualEntitlementLimitation &&
                                            (entitlementType === EntitlementType.HL ||
                                                entitlementType === EntitlementType.HL_UPDATE) &&
                                            isStringBlank(props.mcUuid))
                                    ) {
                                        const maxAssignable = isMigratedToCommonLicense(license.productId)
                                            ? computeCombinedMaxAssignable(license.productId, license.index)
                                            : license.available;
                                        createAssignAmountErrorMessage(
                                            license.assign,
                                            maxAssignable,
                                            license.index,
                                            false
                                        );
                                    }
                                }}
                                min={1}
                                disabled={license.loading}
                                value={license.assign}
                                data-testid={
                                    testIds.workArea.license.entitlements.createEntitlementDialog.licensesTable
                                        .assignedAmountInput.itself
                                }
                            />
                            {license.errorMessage}
                        </div>,
                        <div className={classNames(form.formFields, style.gridColumns)} key={license.index}>
                            <div className={style.formElement}>{formatDateWithoutTime(license.expirationDate)}</div>
                            <div
                                key={"deleteRow" + license.index}
                                data-testid={
                                    testIds.workArea.license.entitlements.createEntitlementDialog.deleteRowButton
                                }
                                className={style.icon}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.LICENSE_ENTITLEMENT,
                                        action: Action.REMOVE_LICENSE,
                                        label: license.productId,
                                    });
                                    removeRow(license.index);
                                }}
                            >
                                <SimpleDeleteIcon color={props.theme.errorBackgroundColor} />
                            </div>
                        </div>,
                    ];
                });

                return (
                    <Form id={"addEntitlementForm"} className={style.fixedWidthModal}>
                        <Heading
                            tag="div"
                            variant="SUBTITLE_1"
                            className={style.modalSubHeading}
                            disableTopSpacing={true}
                        >
                            {t("AddEntitlementForm.addEntitlementFormSubtitle")}
                        </Heading>
                        <div className={form.formFieldsColumnDirection}>
                            <Box marginTop={"Md"}>
                                <label
                                    htmlFor="type"
                                    className={classNames(form.label, {
                                        [form.inputError]: errors.type,
                                    })}
                                >
                                    {t("AddEntitlementForm.type")}
                                </label>
                                <div className={form.formFieldsColumnDirection}>
                                    <select
                                        id="type"
                                        className={classNames(form.select, form.fixedWidthInput, {
                                            [form.inputError]: errors.type,
                                        })}
                                        onChange={(e) => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.LICENSE_ENTITLEMENT,
                                                action: Action.CHANGE_ENTITLEMENT_TYPE,
                                                label: e.target.value,
                                            });
                                            handleChange(e);
                                            setEntitlementType(e.target.value as EntitlementType);
                                        }}
                                        onBlur={handleBlur}
                                        value={values.type}
                                        data-testid={
                                            testIds.workArea.license.entitlements.createEntitlementDialog.typeSelect
                                                .itself
                                        }
                                        disabled={isForMc()}
                                    >
                                        {createEntitlementOptions()}
                                    </select>
                                    <div
                                        className={form.error}
                                        data-testid={
                                            testIds.workArea.license.entitlements.createEntitlementDialog.typeSelect
                                                .errorLabel
                                        }
                                    >
                                        <ErrorMessage name="type" />
                                    </div>
                                    {(values.type === EntitlementType.SL_UPDATE ||
                                        values.type === EntitlementType.SL_CREATE) &&
                                        !isForMc() && (
                                            <Box paddingTop={"Xs"} paddingBottom={"Xs"}>
                                                {bmcEolBanner}
                                            </Box>
                                        )}
                                </div>
                            </Box>
                            {values.type === EntitlementType.SL_UPDATE ? (
                                <div className={classNames(form.formFields)}>
                                    <label
                                        htmlFor="containerId"
                                        className={classNames(form.label, {
                                            [form.inputError]: errors.containerId,
                                        })}
                                    >
                                        {t("AddEntitlementForm.containerId")}
                                    </label>
                                    <div className={form.formFieldsColumnDirection}>
                                        <select
                                            id="containerId"
                                            className={classNames(form.select, form.fixedWidthInput, {
                                                [form.inputError]: errors.containerId,
                                            })}
                                            onChange={(e) => {
                                                usageStatisticsService.sendEvent({
                                                    category: Category.LICENSE_ENTITLEMENT,
                                                    action: Action.CHANGE_ENTITLEMENT_CONTAINER,
                                                });
                                                handleChange(e);
                                            }}
                                            onBlur={handleBlur}
                                            value={values.containerId}
                                            data-testid={
                                                testIds.workArea.license.entitlements.createEntitlementDialog
                                                    .containerSelect.itself
                                            }
                                            disabled={isStringNotBlank(props.mcContainerId)}
                                        >
                                            {props.slContainers.length > 1 && (
                                                <option
                                                    key={DEFAULT_SELECT_CONTAINER_VALUE}
                                                    value={DEFAULT_SELECT_CONTAINER_VALUE}
                                                >
                                                    {t("AddEntitlementForm.selectContainer")}
                                                </option>
                                            )}
                                            {props.slContainers.map((container) => (
                                                <option key={container.containerId} value={container.containerId}>
                                                    {container.name !== null && container.name !== ""
                                                        ? container.name
                                                        : container.containerId}
                                                </option>
                                            ))}
                                        </select>
                                        <div
                                            className={form.error}
                                            data-testid={
                                                testIds.workArea.license.entitlements.createEntitlementDialog
                                                    .containerSelect.errorLabel
                                            }
                                        >
                                            <ErrorMessage name="containerId" />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {values.type === EntitlementType.SL_CREATE ? (
                                <>
                                    <div>
                                        <label
                                            htmlFor="availableSlActivations"
                                            className={classNames(form.label, style.slActivationLabel)}
                                        >
                                            {t("AddEntitlementForm.availableSlActivations")}
                                        </label>
                                        <label id="availableSlActivations" className={form.label}>
                                            {props.emsConfiguration.availableSlActivations}
                                        </label>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="usedSlActivations"
                                            className={classNames(form.label, style.slActivationLabel)}
                                        >
                                            {t("AddEntitlementForm.usedSlActivations")}
                                        </label>
                                        <label id="usedSlActivations" className={form.label}>
                                            {props.emsConfiguration.usedSlActivations}
                                        </label>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className={form.formFields}>
                            <div className={form.formFieldsColumnDirection}>
                                <span className={form.optional}>{t("Common.optional")}</span>
                                <label htmlFor="description" className={classNames(form.label)}>
                                    {t("AddEntitlementForm.description")}
                                </label>
                            </div>
                            <div className={classNames(style.gridRows, form.notes)}>
                                <textarea
                                    id="description"
                                    className={classNames(form.input, form.fixedWidthInput, form.textAreaHeight)}
                                    onChange={handleChange}
                                    data-testid={
                                        testIds.workArea.license.entitlements.createEntitlementDialog
                                            .descriptionTextArea.itself
                                    }
                                    maxLength={DESCRIPTION_MAX_LENGTH}
                                />
                                <span className={classNames(form.optional)}>
                                    {t("AddEntitlementForm.charactersRemaining", {
                                        remainingCharacters: (
                                            DESCRIPTION_MAX_LENGTH - values.description.length
                                        ).toString(),
                                        maximumNumberOfCharacters: DESCRIPTION_MAX_LENGTH.toString(),
                                    })}
                                </span>
                            </div>
                        </div>
                        <Heading tag="div" variant="SUBTITLE_1" className={style.modalSubHeading}>
                            {t("AddEntitlementForm.addEntitlementFormSubtitle2")}
                        </Heading>
                        {blanccoTokenLicense && (
                            <div>
                                <span className={style.tokenMessage}>
                                    {t("AddEntitlementForm.tokenMessage", {
                                        tokenAmount: initialTokenAmount.toString(),
                                    })}
                                </span>
                            </div>
                        )}
                        <StaticTable
                            testId={testIds.workArea.license.entitlements.createEntitlementDialog.licensesTable.itself}
                            headers={[
                                {
                                    className: style.columnHeader,
                                    value: t("AddEntitlementForm.table.license"),
                                },
                                blanccoTokenLicense
                                    ? {
                                          className: style.columnHeader,
                                          value: (
                                              <Tooltip content={t("AddEntitlementForm.table.tokenAmountTooltip")}>
                                                  <span className={style.info}>
                                                      <div className={style.infoText}>
                                                          {t("AddEntitlementForm.table.tokenAmount")}
                                                      </div>
                                                      <Info
                                                          borderColor={theme.contentBackgroundColor}
                                                          color={theme.iconFillColor}
                                                      />
                                                  </span>
                                              </Tooltip>
                                          ),
                                      }
                                    : {
                                          className: style.columnHeader,
                                          value: t("AddEntitlementForm.table.available"),
                                      },
                                {
                                    className: style.columnHeader,
                                    value: blanccoTokenLicense ? (
                                        <Tooltip content={t("AddEntitlementForm.table.amountToAssignTooltip")}>
                                            <span className={style.info}>
                                                <div className={style.infoText}>
                                                    {t("AddEntitlementForm.table.amountToAssign")}
                                                </div>
                                                <Info
                                                    borderColor={theme.contentBackgroundColor}
                                                    color={theme.iconFillColor}
                                                />
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        t("AddEntitlementForm.table.amountToAssign")
                                    ),
                                },
                                {
                                    className: style.columnHeader,
                                    value: (
                                        <div className={style.infoText}>
                                            {t("AddEntitlementForm.table.expirationDate")}
                                        </div>
                                    ),
                                },
                            ]}
                            cells={availableLicenses.length > 0 ? cells : []}
                        />
                        {availableLicenses.length < 1 ? (
                            <></>
                        ) : (
                            <div className={classNames(style.gridRows, style.buttons)}>
                                <button
                                    // Prevent this button from acting as a form submit button when it's the only
                                    // enabled button. Reference: https://github.com/jaredpalmer/formik/issues/1610
                                    type="button"
                                    onClick={addRow}
                                    className={classNames(style.link, buttons.textButton)}
                                    data-testid={
                                        testIds.workArea.license.entitlements.createEntitlementDialog.addRowButton
                                    }
                                    hidden={hideAddRowButton()}
                                >
                                    {addMoreLicenses(
                                        props.theme.contentBackgroundColor,
                                        props.theme.iconFillColor,
                                        t("Common.addMoreLicenses")
                                    )}
                                </button>

                                <button
                                    // See the comment for the above button.
                                    type="button"
                                    onClick={clearAllRows}
                                    className={classNames(style.link, buttons.textButton)}
                                    data-testid={
                                        testIds.workArea.license.entitlements.createEntitlementDialog.clearAllButton
                                    }
                                >
                                    {t("AddEntitlementForm.table.clearAll")}
                                </button>
                            </div>
                        )}
                        <div>
                            {(errorFlag || hlLimitError) && (
                                <div className={classNames(style.errorText)}>
                                    <FailedRedNotificationIcon
                                        backgroundColor={props.theme.errorIconColor}
                                        iconColor={props.theme.contentBackgroundColor}
                                    />
                                    <span>
                                        {hlLimitError
                                            ? t("AddEntitlementForm.licenseLimitMessage", {
                                                  maximumLimit: MAXIMUM_LICENSES_FOR_HL,
                                              })
                                            : t("AddEntitlementForm.table.maximumNumberOfAssignedTokens", {
                                                  availableAmount: initialTokenAmount,
                                                  maximumAmount: initialTokenAmount,
                                              })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}

export default connector(AddEntitlementForm);
